/**
 * Apple Sign-In for Chrome/Safari extensions.
 *
 * Chrome extensions cannot use Firebase's signInWithPopup because the extension
 * page's CSP and cross-origin restrictions block the popup/postMessage mechanism.
 * This module uses browser.identity.launchWebAuthFlow instead — the same approach
 * used by chromeIdentityAuth.ts for Google.
 *
 * Setup required (one-time, in Apple Developer portal):
 *   1. Create a Services ID (e.g. com.rehmn.signin) and enable "Sign In with Apple".
 *   2. Under that Services ID, add the extension's redirect URL as a Return URL:
 *        Chrome: the value of browser.identity.getRedirectURL()
 *        (looks like https://<extension-id>.chromiumapp.org/)
 *   3. Set REACT_APP_APPLE_SERVICE_ID in your .env to the Services ID.
 */

import {
  OAuthProvider,
  signInWithCredential,
  setPersistence,
  indexedDBLocalPersistence,
} from 'firebase/auth/web-extension';
import { auth } from '../config/firebaseConfig';
import { ext, isExtensionPage } from '../utils/browserApi';
import { signInWithApple as firebaseAppleSignIn } from './authService';
import { signInViaSafariRelay, isAccountUpgradePage, ACCOUNT_UPGRADE_URL } from './safariRelayAuth';
import { log, error as logError } from '../utils/logger';

export interface AppleAuthResult {
  user: {
    uid: string;
    displayName: string | null;
    email: string | null;
    photoURL: string | null;
    providerId: string;
  } | null;
  error?: string;
}

// Services ID registered in Apple Developer portal for Sign In with Apple.
// Must match exactly — this is NOT the App Bundle ID.
const APPLE_SERVICE_ID = process.env.REACT_APP_APPLE_SERVICE_ID || '';

/** SHA-256 hash of a string using the Web Crypto API (available in extensions). */
async function sha256Hex(message: string): Promise<string> {
  const encoded = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Cryptographically random hex string for use as a nonce. */
function generateRawNonce(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Sign in with Apple using launchWebAuthFlow.
 * Falls back to Firebase signInWithPopup when not in an extension context
 * (e.g. localhost dev server).
 */
export const signInWithAppleIdentity = async (): Promise<AppleAuthResult> => {
  // Localhost / non-extension fallback
  if (!isExtensionPage()) {
    if (isAccountUpgradePage()) {
      // Hosted upgrade page: popup OAuth is dev-only — use the hosted relay.
      log('[AppleIdentity] Account upgrade page: using relay sign-in...');
      return signInViaSafariRelay('apple', ACCOUNT_UPGRADE_URL);
    }
    log('[AppleIdentity] Not in extension context, using Firebase popup fallback...');
    return firebaseAppleSignIn();
  }

  // Safari may not expose the identity API even on extension pages.
  // Fall back to the hosted relay when launchWebAuthFlow is unavailable.
  if (typeof (ext as any)?.identity?.launchWebAuthFlow !== 'function') {
    log('[AppleIdentity] identity API unavailable, using relay sign-in...');
    return signInViaSafariRelay('apple');
  }

  if (!APPLE_SERVICE_ID) {
    const msg =
      'Apple Service ID not configured. ' +
      'Set REACT_APP_APPLE_SERVICE_ID in .env to your Apple Services ID ' +
      '(e.g. com.rehmn.signin) and register the extension redirect URL in the Apple Developer portal.';
    logError('[AppleIdentity]', msg);
    return { user: null, error: msg };
  }

  try {
    const rawNonce = generateRawNonce();
    const hashedNonce = await sha256Hex(rawNonce);
    const redirectUrl = (ext as any).identity.getRedirectURL();

    log('[AppleIdentity] Redirect URL:', redirectUrl);

    // Apple Sign In OAuth 2.0 + OIDC endpoint.
    // response_type=code id_token + response_mode=fragment returns the id_token in the
    // URL hash so launchWebAuthFlow can capture it without a server round-trip.
    const authUrl = new URL('https://appleid.apple.com/auth/authorize');
    authUrl.searchParams.set('client_id', APPLE_SERVICE_ID);
    authUrl.searchParams.set('redirect_uri', redirectUrl);
    authUrl.searchParams.set('response_type', 'code id_token');
    authUrl.searchParams.set('response_mode', 'fragment');
    authUrl.searchParams.set('scope', 'openid');
    authUrl.searchParams.set('nonce', hashedNonce);

    log('[AppleIdentity] Launching Apple web auth flow...');

    return await new Promise<AppleAuthResult>((resolve) => {
      (ext as any).identity.launchWebAuthFlow(
        { url: authUrl.toString(), interactive: true },
        async (responseUrl: string | undefined) => {
          if (ext.runtime?.lastError) {
            const msg = (ext.runtime.lastError as any).message || 'Apple authentication failed';
            logError('[AppleIdentity] launchWebAuthFlow error:', msg);
            resolve({ user: null, error: msg });
            return;
          }

          if (!responseUrl) {
            resolve({ user: null, error: 'No response from Apple authentication' });
            return;
          }

          log('[AppleIdentity] Auth flow completed, parsing response...');

          // Apple returns params in the URL fragment when using response_mode=fragment
          const url = new URL(responseUrl);
          const fragment = new URLSearchParams(url.hash.substring(1));
          const idToken = fragment.get('id_token');

          if (!idToken) {
            logError('[AppleIdentity] No id_token in response. Full URL:', responseUrl);
            resolve({
              user: null,
              error:
                'Apple did not return an id_token. ' +
                'Make sure the extension redirect URL is registered as a Return URL ' +
                'in the Apple Developer portal for your Services ID.',
            });
            return;
          }

          try {
            const provider = new OAuthProvider('apple.com');
            // rawNonce must match the nonce that was hashed and sent to Apple
            const credential = provider.credential({ idToken, rawNonce });

            await setPersistence(auth, indexedDBLocalPersistence);
            const result = await signInWithCredential(auth, credential);

            log('[AppleIdentity] Firebase sign-in successful');
            resolve({
              user: {
                uid: result.user.uid,
                displayName: result.user.displayName || null,
                email: result.user.email || null,
                photoURL: result.user.photoURL || null,
                providerId: 'apple.com',
              },
            });
          } catch (credErr: any) {
            logError('[AppleIdentity] Firebase credential error:', credErr);
            resolve({
              user: null,
              error: credErr?.message || 'Failed to sign in with Apple credential',
            });
          }
        },
      );
    });
  } catch (err) {
    logError('[AppleIdentity] Unexpected error:', err);
    return {
      user: null,
      error: err instanceof Error ? err.message : String(err),
    };
  }
};
